import type { CrestPalette, CrestSigil, PlayerCrest, PublicCitadel, PublicPrestige, RaceKey } from "../types";

const PEOPLE_LABELS: Record<RaceKey, string> = {
  cendriens: "Cendriens",
  sylphes: "Sylphes",
  karak: "Karak",
  golgoths: "Golgoths",
  grisepouce: "Grisepouce",
};

const FALLBACK_SIGILS = ["✦", "◆", "♜", "☽", "⚜", "✧"] as const;

export type ShowcasePortrait = "herald" | "people" | "living";
export type ShowcaseFrame = "iron" | "ember" | "first-camp";
export type ShowcaseNameColor = "ivory" | "ember" | "moon";

export const COSMETIC_SCENE_TIERS = [
  {
    key: "standard",
    label: "Standard",
    access: "included",
    promise: "Citadelle PBR, affiche publique, classement et cadrage vivant.",
  },
  {
    key: "signature",
    label: "Signature",
    access: "entitlement",
    promise: "Environnement thématique, aura, entrée animée et identité reconnaissable.",
  },
  {
    key: "legendary",
    label: "Scène légendaire",
    access: "future",
    promise: "Région complète, séquence narrative et identité sonore dédiée.",
  },
] as const;

export interface SupporterFanalPack {
  readonly id: string;
  readonly displayName: string;
  readonly status: "LOCAL_PREVIEW_NOT_COMMERCIALIZED";
  readonly environmentTreatment: string;
  readonly arrivalTreatment: string;
  readonly frameTreatment: string;
  readonly nameTreatment: string;
  readonly supportMarkTreatment: string;
  readonly truthCopy: string;
}

export const SUPPORTER_FANAL_PACK = Object.freeze({
  id: "supporter-fanal",
  displayName: "Le Fanal du Bastion",
  status: "LOCAL_PREVIEW_NOT_COMMERCIALIZED",
  environmentTreatment: "Une veille lumineuse contenue autour de la Citadelle PBR.",
  arrivalTreatment: "Une arrivée cérémonielle courte, skippable et rejouable.",
  frameTreatment: "Un cadre discret de soutien, propre à l’aperçu.",
  nameTreatment: "Un traitement chaleureux et lisible du pseudonyme.",
  supportMarkTreatment: "Une marque discrète et accessible : Soutien de VAELOR.",
  truthCopy: "Aperçu local · non commercialisé. Strictement cosmétique, sans effet sur le jeu.",
} as const satisfies SupporterFanalPack);

export const SHOWCASE_PORTRAITS: ReadonlyArray<{
  key: ShowcasePortrait;
  label: string;
  access: "included" | "premium";
  animated: boolean;
}> = [
  { key: "herald", label: "Héraut du peuple", access: "included", animated: false },
  { key: "people", label: "Portrait du serment", access: "included", animated: false },
  { key: "living", label: "Héraut vivant", access: "premium", animated: true },
];

export const SHOWCASE_FRAMES: ReadonlyArray<{
  key: ShowcaseFrame;
  label: string;
  access: "included" | "premium" | "honor";
}> = [
  { key: "iron", label: "Fer du Bastion", access: "included" },
  { key: "ember", label: "Braise souveraine", access: "premium" },
  { key: "first-camp", label: "Vainqueur du Premier Camp", access: "honor" },
];

export const SHOWCASE_NAME_COLORS: ReadonlyArray<{
  key: ShowcaseNameColor;
  label: string;
  access: "included" | "premium";
}> = [
  { key: "ivory", label: "Ivoire", access: "included" },
  { key: "ember", label: "Braise", access: "premium" },
  { key: "moon", label: "Pierre de lune", access: "premium" },
];

export const CREST_SIGIL_PRESETS: ReadonlyArray<{ key: CrestSigil; label: string; glyph: string }> = [
  { key: "tower", label: "Tour du Serment", glyph: "♜" },
  { key: "flame", label: "Flamme d’Ignis", glyph: "◆" },
  { key: "moon", label: "Lune du Voile", glyph: "☽" },
  { key: "thorn", label: "Épine du Miasme", glyph: "✦" },
  { key: "crown", label: "Couronne des Anneaux", glyph: "⚜" },
  { key: "star", label: "Étoile du Fanal", glyph: "✧" },
];

export const CREST_PALETTE_PRESETS: ReadonlyArray<{ key: CrestPalette; label: string; primary: string; accent: string }> = [
  { key: "ember", label: "Braise souveraine", primary: "#6f2418", accent: "#f3b45f" },
  { key: "royal", label: "Pourpre royal", primary: "#421d4f", accent: "#d8a5e8" },
  { key: "miasma", label: "Voile du Miasme", primary: "#241946", accent: "#a98cff" },
  { key: "verdant", label: "Serment verdoyant", primary: "#173d31", accent: "#8fd8ab" },
  { key: "moonstone", label: "Pierre de lune", primary: "#243342", accent: "#bcdcf4" },
];

function stableHash(value: string): number {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function visualSeedForCitadel(publicId: string, displayName: string, sectorId: string): number {
  return stableHash(`${publicId}:${displayName}:${sectorId}`);
}

export function peopleLabel(people: RaceKey | null): string {
  return people ? PEOPLE_LABELS[people] : "Peuple non révélé";
}

export function reputationForRing(bestRing: number): string {
  if (bestRing <= 2) return "Fanal légendaire";
  if (bestRing <= 4) return "Renommée des Anneaux";
  if (bestRing <= 6) return "Bastion établi";
  return "Fanal émergent";
}

export function formatPrestigePoints(points: number): string {
  const safePoints = Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;
  return `${safePoints.toLocaleString("fr-FR")} pts`;
}

export function formatPrestigeRank(rank: number | null): string {
  if (rank === null || !Number.isInteger(rank) || rank < 1) return "Hors classement";
  return rank === 1 ? "1er mondial" : `${rank}e mondial`;
}

export function normalizePublicPrestige(prestige: PublicPrestige | null | undefined): PublicPrestige {
  if (!prestige) return { points: 0, rank: null, version: 1, honors: [] };
  return {
    points: Number.isFinite(prestige.points) ? Math.max(0, Math.trunc(prestige.points)) : 0,
    rank: Number.isInteger(prestige.rank) && (prestige.rank ?? 0) > 0 ? prestige.rank : null,
    version: 1,
    honors: Array.isArray(prestige.honors) && prestige.honors.includes("first-camp") ? ["first-camp"] : [],
  };
}

export function orderPublicCitadelsByPrestige(citadels: readonly PublicCitadel[]): PublicCitadel[] {
  return [...citadels].sort((left, right) => {
    const leftPrestige = normalizePublicPrestige(left.prestige);
    const rightPrestige = normalizePublicPrestige(right.prestige);
    const leftRank = leftPrestige.rank ?? Number.POSITIVE_INFINITY;
    const rightRank = rightPrestige.rank ?? Number.POSITIVE_INFINITY;
    return leftRank - rightRank ||
      rightPrestige.points - leftPrestige.points ||
      left.displayName.localeCompare(right.displayName, "fr");
  });
}

export function portraitForPeople(people: RaceKey | null, portrait: ShowcasePortrait = "herald"): string {
  if (!people) return "/game/herald_default.webp";
  const safePeople = people;
  return portrait === "people" ? `/game/race_${safePeople}.webp` : `/game/herald_${safePeople}.webp`;
}

export function crestForCitadel(publicId: string, people: RaceKey | null, selected?: PlayerCrest | null) {
  if (selected) {
    const sigil = CREST_SIGIL_PRESETS.find((preset) => preset.key === selected.sigil);
    const palette = CREST_PALETTE_PRESETS.find((preset) => preset.key === selected.palette);
    if (sigil && palette) return { primary: palette.primary, accent: palette.accent, sigil: sigil.glyph };
  }
  const hash = stableHash(`${publicId}:${people ?? "unknown"}`);
  const hue = 12 + (hash % 42);
  const accentHue = (hue + 190 + ((hash >>> 8) % 28)) % 360;
  return {
    primary: `hsl(${hue} 58% 30%)`,
    accent: `hsl(${accentHue} 52% 64%)`,
    sigil: FALLBACK_SIGILS[(hash >>> 16) % FALLBACK_SIGILS.length],
  };
}
